/**
 * app.js
 *
 * This is the entry file for the application, only setup and boilerplate
 * code.
 */

import React from 'react';
import { createRoot } from 'react-dom/client';
import { StyleSheetManager, ThemeProvider } from 'styled-components';
import { Provider } from 'react-redux';
import { Router, browserHistory } from 'react-router';
import FontFaceObserver from 'fontfaceobserver';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import 'draft-js/dist/Draft.css';
import 'draft-js-mention-plugin/lib/plugin.css';
import 'react-datepicker/dist/react-datepicker.css';

import { BaukastenContext, shouldForwardProp } from 'baukasten';
import theme from 'styles/theme';

import { dispatchLocationChangesToStore } from 'utils/routerRedux';
// Import Language Provider
import LanguageProvider from 'containers/LanguageProvider';
import { GraphQLClientProvider } from 'utils/graphql/client/GraphQLClientProvider';
import { RouteBootstrap } from 'features/appBootstrap/RouteBootstrap';
import { LicenseUpgradeModalProvider } from 'features/licenseUpgrade/LicenseUpgradeModalContext';

import configureStore from './store';
// Import i18n messages
import { translationMessages } from './i18n';
// Import root routes
import createRoutes from './routes';
// Import CSS reset and Global Styles
import GlobalStyles from './global-styles';
import ToolTipGlobalStyles from './components/ToolTip/globalStyles';
import './components/InputDayPicker/globalStyles';

// Load the favicon
import './favicon.png';

// Import sentry.io error tracker

if (
  process.env.NODE_ENV === 'production' &&
  process.env.SENTRY_URL !== 'review'
) {
  require('utils/sentry'); // eslint-disable-line global-require
}

// Observe loading of Lato (to remove lato, remove the <link> tag in
// the index.html file and this observer)
const latoObserver = new FontFaceObserver('Lato', {});

// When Open Sans is loaded, add a font-family using Open Sans to the body
latoObserver.load().then(
  () => {
    document.body.classList.add('fontLoaded');
  },
  () => {
    document.body.classList.remove('fontLoaded');
  }
);

// Create redux store with history
// this uses the singleton browserHistory provided by react-router
const store = configureStore(browserHistory);

// Mirror history changes into the store so reducers, middleware and sagas
// keep receiving @@router/LOCATION_CHANGE actions. Must be wired before the
// Router mounts so the store listener registers first.
dispatchLocationChangesToStore(browserHistory, store);

browserHistory.listen(location => {
  if (window.swUpdate) {
    window.location = location.pathname + location.search;
  }
});

const MOUNT_NODE = document.getElementById('app');
const root = createRoot(MOUNT_NODE);

// Set up the router, wrapping all Routes in the App component
const routes = {
  component: RouteBootstrap,
  childRoutes: createRoutes(store),
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        if (error?.response?.status === 403) {
          return false;
        }

        if (failureCount === 2) {
          return false;
        }

        return true;
      },
    },
  },
});

const render = messages => {
  root.render(
    <Provider store={store}>
      <StyleSheetManager shouldForwardProp={shouldForwardProp}>
        <GlobalStyles />
        {/* Global styles for the legacy react-tooltip based ToolTip, injected
            once here: rendering them per ToolTip instance thrashes the
            stylesheet under styled-components v6. Delete once the last
            components/ToolTip usage is migrated to baukasten Tooltip. */}
        <ToolTipGlobalStyles />
        <ThemeProvider theme={theme}>
          <LanguageProvider messages={messages}>
            <QueryClientProvider client={queryClient}>
              <GraphQLClientProvider>
                <BaukastenContext.Provider
                  value={{ navigateTo: browserHistory.push }}
                >
                  <LicenseUpgradeModalProvider>
                    <Router history={browserHistory} routes={routes} />
                    {process.env.NODE_ENV === 'development' && (
                      <ReactQueryDevtools
                        initialIsOpen={false}
                        toggleButtonProps={{ style: { maxWidth: 24 } }}
                        position="bottom-right"
                      />
                    )}
                  </LicenseUpgradeModalProvider>
                </BaukastenContext.Provider>
              </GraphQLClientProvider>
            </QueryClientProvider>
          </LanguageProvider>
        </ThemeProvider>
      </StyleSheetManager>
    </Provider>
  );
};

render(translationMessages);
